import React, { useCallback, useContext } from 'react';
import Select, { ValueType } from 'react-select';
import { styleFn, Styles } from 'react-select/src/styles';
import { defineMessages, useIntl } from 'react-intl';
import deepmerge from 'deepmerge';
import style from './Language.module.scss';
import { ThemeContext } from '../App/App';
import { getLanguageInfo } from '../../utilities/languageNames';

const messages = defineMessages({
	next: {
		id: 'base.next',
		defaultMessage: 'Next'
	},
	selectLanguage: {
		id: 'base.select_language',
		defaultMessage: 'Select language'
	},
	chooseLanguage: {
		id: 'base.choose_language',
		defaultMessage: 'Please choose a language for your verification'
	}
});

interface LanguageProps {
	handleLanguageSelect: (lang: string) => void;
	currentLanguage: string;
	allowedLanguages: string[] | undefined;
}

interface LanguageOption {
	label: string;
	value: string;
}

export default ({ handleLanguageSelect, currentLanguage, allowedLanguages }: LanguageProps) => {
	const { selectInput } = useContext(ThemeContext);
	const { formatMessage } = useIntl();

	const handleLanguageChange = useCallback((option: ValueType<LanguageOption>) => {
		if (option) handleLanguageSelect((option as LanguageOption).value);
	}, []);

	const langOptions: LanguageOption[] | undefined = allowedLanguages
		?.map((language: string) => ({
			label: `${getLanguageInfo(language).nativeName} (${language
				.toUpperCase()
				.substring(language.length - 2, language.length)})`,
			value: language
		}))
		// sort languages alphabetically
		.slice(0)
		.sort((a, b) => {
			const x = a.label.toLowerCase();
			const y = b.label.toLowerCase();
			return x < y ? -1 : x > y ? 1 : 0;
		});

	const englishLanguage = {
		label: `${getLanguageInfo('en').nativeName} (EN)`,
		value: 'en'
	};

	const getInitialLanguage = () => {
		if (!langOptions || langOptions?.length === 0) {
			handleLanguageChange(englishLanguage);
			return englishLanguage;
		}
		if (langOptions.length === 1) {
			handleLanguageChange(langOptions[0]);
			return langOptions[0];
		}
		return (
			langOptions.find(langOption => langOption.value === currentLanguage) || englishLanguage
		);
	};

	const selectInputStyle: Styles = {
		...selectInput.styles,
		control: (...params) => ({
			...(selectInput.styles.control as styleFn)(...params),
			width: '100%',
			maxWidth: '200px',
			height: '38px',
			minWidth: '100px',
			fontSize: '13px',
			fontWeight: 600,
			border: '0',
			outline: 'none'
		}),
		menu: (...params) => ({
			...(selectInput.styles.menu as styleFn)(...params),
			width: '100%',
			maxWidth: '200px',
			fontSize: '13px',
			fontWeight: 600,
			boxShadow: '0 0 25px #e2e2e2',
			border: '0'
		}),
		menuList: (...params) => ({
			...(selectInput.styles.menuList as styleFn)(...params),
			opacity: 1,
			'::-webkit-scrollbar': {
				width: '8px'
			},
			'::-webkit-scrollbar-thumb': {
				background: params[1].theme.colors.primary50,
				borderRadius: '4px'
			},
			'::-webkit-scrollbar-thumb:hover': {
				background: params[1].theme.colors.primary75
			}
		}),
		option: (...params) => {
			return {
				...params[0],
				backgroundColor: 'none',
				cursor: 'pointer',
				color: params[1].theme.colors.primary,
				'&:hover': {
					backgroundColor: 'none',
					color: params[1].theme.colors.primary75
				}
			};
		},
		singleValue: (...params) => ({
			color: params[1].theme.colors.primary
		})
	};

	if (!langOptions || langOptions.length < 2) {
		return null;
	}

	return (
		<>
			<div className={style.languageSelect}>
				<div className={style.selectContainer}>
					<Select
						value={
							langOptions.find(option => option.value === currentLanguage) ||
							englishLanguage
						}
						options={langOptions}
						name="language"
						id="language"
						defaultValue={getInitialLanguage()}
						placeholder={formatMessage(messages.selectLanguage)}
						onChange={option => handleLanguageChange(option)}
						styles={selectInputStyle}
						theme={theme => deepmerge(theme, selectInput.theme)}
					/>
				</div>
			</div>
		</>
	);
};
